import { ThemeProvider as MaterialThemeProvider, createTheme } from "@mui/material/styles";

export type colorTone = { main: string; dark: string; pale: string };

export type gradient = {
  "1": colorTone;
  "2": colorTone;
  "3": colorTone;
  "4": colorTone;
  "5": colorTone;
  "6": colorTone;
  "7": colorTone;
  "8": colorTone;
};

declare module "@mui/material/styles" {
  interface Theme {
    colors: {
      seanceToDo: string;
      seanceDone: string;
      seanceReadyToBill: string;
      seancePutOnHold: string;
      seanceBilled: string;
      primary: string;
      light: string;
      dark: string;
      secondary: string;
      lightgray: string;
      background: string;
      bgPale: string;
      text: string;
      selected: string;
      seanceStatus: {
        "to-do": string;
        done: string;
        "ready-to-bill": string;
        "put-on-hold": string;
        billed: string;
      };
      blue: colorTone;
      blueSky: colorTone;
      green: colorTone;
      grey: colorTone;
      magenta: colorTone;
      orange: colorTone;
      pink: colorTone;
      purple: colorTone;
      purplePink: colorTone;
      red: colorTone;
      white: string;
      yellow: colorTone;
      gradient: {
        "0": colorTone;
        "1": colorTone;
        "2": colorTone;
        "3": colorTone;
        "4": colorTone;
        "5": colorTone;
        "6": colorTone;
        "7": colorTone;
        "8": colorTone;
      };
      revertGradient: {
        "0": colorTone;
        "1": colorTone;
        "2": colorTone;
        "3": colorTone;
        "4": colorTone;
        "5": colorTone;
        "6": colorTone;
        "7": colorTone;
        "8": colorTone;
      };
    };
    fonts: {
      body: string;
      heading: string;
      monospace: string;
    };
  }
  interface ThemeOptions {
    colors: {
      seanceToDo: string;
      seanceDone: string;
      seanceReadyToBill: string;
      seancePutOnHold: string;
      seanceBilled: string;
      primary: string;
      light: string;
      dark: string;
      secondary: string;
      lightgray: string;
      background: string;
      bgPale: string;
      text: string;
      selected: string;
      seanceStatus: {
        "to-do": string;
        done: string;
        "ready-to-bill": string;
        "put-on-hold": string;
        billed: string;
      };
      blue: colorTone;
      blueSky: colorTone;
      green: colorTone;
      grey: colorTone;
      magenta: colorTone;
      orange: colorTone;
      pink: colorTone;
      purple: colorTone;
      purplePink: colorTone;
      red: colorTone;
      white: string;
      yellow: colorTone;

      gradient: {
        "0": colorTone;
        "1": colorTone;
        "2": colorTone;
        "3": colorTone;
        "4": colorTone;
        "5": colorTone;
        "6": colorTone;
        "7": colorTone;
        "8": colorTone;
      };
      revertGradient: {
        "0": colorTone;
        "1": colorTone;
        "2": colorTone;
        "3": colorTone;
        "4": colorTone;
        "5": colorTone;
        "6": colorTone;
        "7": colorTone;
        "8": colorTone;
      };
    };
    fonts: {
      body: string;
      heading: string;
      monospace: string;
    };
  }
}

export const colorPalette = {
  blue: { main: "#8ebaef", dark: "#3b6ead", pale: "#d7e4f2" },
  blueSky: { main: "#68d5f2", dark: "#2198b9", pale: "#d6e9ed" },
  green: { main: "#6CE9AE", dark: "#02aa80", pale: "#e1f4ef" },
  grey: { main: "#4F4F4F", dark: "#212121", pale: "#ebebeb" },
  magenta: { main: "#FF008D", dark: "#c3006b", pale: "#ffcae7", 100: "#fce7f3" },
  orange: { main: "#f2af78", dark: "#de7723", pale: "#f9ddc7" },
  pink: { main: "#fcaeb6", dark: "#ff7684", pale: "#ffe6ea" },
  purple: { main: "#bca2ed", dark: "#9161eb", pale: "#e8e6ef" },
  purplePink: { main: "#ed9fe2", dark: "#d065c2", pale: "#f2e4f2" },
  red: { main: "#e15757", dark: "#900606", pale: "#ffa8a8" },
  white: "#ffffff",
  yellow: { main: "#f4cb1c", dark: "#b29b35", pale: "#fff0b1" },
};

export function AppThemeProvider({ children }) {
  const theme = createTheme({
    palette: {
      primary: {
        main: colorPalette.magenta.main,
        light: colorPalette.magenta.pale,
        dark: "#ac005c",
        100: colorPalette.magenta[100],
      },
    },
    colors: {
      selected: "#6CE9AE",
      seanceToDo: "#e3e3e3",
      seanceDone: "#ffe196",
      seanceReadyToBill: "#96adff",
      seancePutOnHold: "#ff96c2",
      seanceBilled: "#99ff96",
      primary: colorPalette.magenta.main,
      dark: "#573246",
      light: colorPalette.magenta.pale,
      secondary: colorPalette.magenta.pale,
      lightgray: "#f6f6ff",
      background: "#fff",
      text: "#262626",
      bgPale: "#fef4f9",
      seanceStatus: {
        "to-do": "#e3e3e3",
        done: "#ffe196",
        "ready-to-bill": "#96adff",
        "put-on-hold": "#ff96c2",
        billed: "#99ff96",
      },
      ...colorPalette,
      gradient: {
        "0": colorPalette.blueSky,
        "1": colorPalette.orange,
        "2": colorPalette.green,
        "3": colorPalette.pink,
        "5": colorPalette.blue,
        "6": colorPalette.purple,
        "4": colorPalette.yellow,
        "7": colorPalette.magenta,
        "8": colorPalette.purplePink,
      },
      revertGradient: {
        "0": colorPalette.purplePink,
        "1": colorPalette.magenta,
        "2": colorPalette.purple,
        "3": colorPalette.blue,
        "4": colorPalette.yellow,
        "5": colorPalette.pink,
        "6": colorPalette.green,
        "7": colorPalette.orange,
        "8": colorPalette.blueSky,
      },
    },
    fonts: {
      body: "system-ui, sans-serif",
      heading: "inherit",
      monospace: "Menlo, monospace",
    },
    spacing: [0, 4, 8, 12, 16, 32],
  });

  return <MaterialThemeProvider theme={theme}>{children}</MaterialThemeProvider>;
}
